import { CommonModule } from '@angular/common';
import {
  Component,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Profile, ProfileApi } from '@venecia/apis/identity';
import { ErrorMessageService, MessageService, TraceService } from '@venecia/services';

import { ResponsiveTable } from '../responsive-table/responsive-table';
import { Column, RowAction } from '../responsive-table/responsive-table.model';
import { Spinner } from '../spinner/spinner';

@Component({
  selector: 'app-profile-grid',
  standalone: true,
  imports: [CommonModule, TranslateModule, ResponsiveTable, Spinner],
  templateUrl: './profile-grid.html',
  styleUrl: './profile-grid.scss',
})
export class ProfileGrid implements OnInit, OnChanges {
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly messageService = inject(MessageService);
  private readonly profileApi = inject(ProfileApi);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  readonly accountId = input<number | null>(null);
  readonly reloadVersion = input<number>(0);
  readonly detailRequested = output<Profile>();
  readonly totalChanged = output<number>();

  readonly loading = signal(false);
  readonly profiles = signal<readonly Profile[]>([]);

  readonly pageNumber = signal(1);
  readonly pageSize = signal(10);
  readonly totalItems = signal(0);

  readonly trackByProfile = (_: number, profile: Profile) =>
    profile.id ?? `${profile.application?.id ?? ''}-${profile.name}`;

  readonly columns = computed<readonly Column<Profile>[]>(() => [
    {
      id: 'application',
      header: this.translateService.instant('pages.profiles.fields.application'),
      value: (profile) => profile.application?.name ?? '',
      nowrap: true,
      width: 40
    },
    {
      id: 'name',
      header: this.translateService.instant('pages.profiles.fields.name'),
      value: (profile) => profile.name,
      width: 50
    },
  ]);

  readonly actions = computed<readonly RowAction<Profile>[]>(() => [
    {
      id: 'details',
      label: this.translateService.instant('pages.profiles.actions.details'),
      icon: 'fa-solid fa-pen-to-square',
    },
  ]);

  ngOnInit(): void {
    void this.loadProfiles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const reloadVersionChange = changes['reloadVersion'];
    if (!reloadVersionChange || reloadVersionChange.firstChange) {
      return;
    }

    this.pageNumber.set(1);
    void this.loadProfiles();
  }

  onActionSelected(ev: { actionId: string; item: Profile }): void {
    if (ev.actionId === 'details') {
      this.detailRequested.emit(ev.item);
    }
  }

  onPageChange(page: number): void {
    if (page === this.pageNumber()) {
      return;
    }

    this.pageNumber.set(page);
    void this.loadProfiles();
  }

  private async loadProfiles(): Promise<void> {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.profileApi.search({
          payload: this.buildSearchPayload(),
        }),
      );

      this.profiles.set(response.body.data ?? []);
      this.totalItems.set(response.body.total ?? 0);
      this.totalChanged.emit(this.totalItems());
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.profiles.load-error'));
    } finally {
      this.loading.set(false);
    }
  }

  private buildSearchPayload(): Record<string, unknown> {
    const accountId = this.accountId();

    return {
      page: this.pageNumber() - 1,
      size: this.pageSize(),
      ...(accountId !== null
        ? {
            filters: [
              {
                field: 'account.id',
                value: accountId,
                comparative: 'eq',
              },
            ],
          }
        : {}),
    };
  }
}
